import { z } from 'zod'
import { Mutator } from './base'
import { DataBase } from '@/lib/kysely'
import { useUserStore } from '@/stores/user'
import type { InsertQuery, UpdateQuery, DeleteQuery } from '@/lib/entity/base'

import { graphql } from '@/gql'
import type {
  Coassign_Job_Trigger_Insert_Input,
  Coassign_Job_Trigger_Set_Input
} from '@/gql/graphql'
import type { MutationOperationAndVariables } from '@/lib/mutationHelpers'
import { pick } from '@/lib/utils/functional'
import { type Reactive } from 'vue'

export const JobTriggerSchema = z.object({
  id: z.string().max(21),
  job_id: z.string().max(21),
  trigger_type: z.string().max(8),
  params: z.string().nullable(),
  claim: z.string().default('{}'),
  timing: z.string().nullable()
})

export type JobTrigger = z.infer<typeof JobTriggerSchema>

export class JobTriggerMutator extends Mutator<typeof JobTriggerSchema> {
  static readonly stageableClassName = 'JobTriggerMutator'
  schema = JobTriggerSchema
  table = 'job_trigger' as const

  static async load(id: string, job_id?: string): Promise<Reactive<JobTriggerMutator>> {
    const self = this.createInstance()
    self.base = await DataBase.selectFrom(self.table)
      .selectAll()
      .where('id', '=', id)
      .executeTakeFirst()

    self.context = { id }

    if (self.base === undefined && job_id !== undefined) {
      self.context.job_id = job_id
      self.modified.claim = self.modified.claim ?? '{}'
    }

    return self
  }

  async updateLocalDB(): Promise<UpdateQuery[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    return [DataBase.updateTable(this.table).set(params).where('id', '=', id).compile()]
  }

  async insertLocalDB(): Promise<InsertQuery[]> {
    const tenant_id = useUserStore().userInfo!.tenantId
    const parsedData = (await this.parsed)!
    const params = {
      ...parsedData,
      tenant_id,
      params: parsedData.params ?? null,
      claim: parsedData.claim ?? '{}'
    }
    return [DataBase.insertInto(this.table).values(params).compile()]
  }

  async deleteLocalDB(): Promise<DeleteQuery[]> {
    const id = this.context.id!
    return [DataBase.deleteFrom(this.table).where('id', '=', id).compile()]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asInsertMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const params = (await this.parsed)!
    const variables: Coassign_Job_Trigger_Insert_Input = {
      ...params,
      params: params.params != null && params.params !== '' ? JSON.parse(params.params) : null,
      claim: params.claim != null && params.claim !== '' ? JSON.parse(params.claim) : {}
    }
    return [{ mutation: insertJobTriggerMutationDocument, variables: { input: variables } }]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asDeleteMutation(commitId: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const variables: Coassign_Job_Trigger_Set_Input = {
      // commit_id: commitId,
      // deleted_at: formatISO(new Date())
    }
    return [{ mutation: updateJobTriggerMutationDocument, variables: { id: id, input: variables } }]
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async asUpdateMutation(commit_id: string): Promise<MutationOperationAndVariables[]> {
    const id = this.context.id!
    const rawData = { ...(await this.parsed)! }
    const params = pick(rawData, Object.keys(this.modified) as (keyof typeof this.modified)[])
    const variables: Coassign_Job_Trigger_Set_Input = {
      ...params,
      params: params.params != null && params.params !== '' ? JSON.parse(params.params) : null,
      claim: params.claim != null && params.claim !== '' ? JSON.parse(params.claim) : {}
    }
    return [{ mutation: updateJobTriggerMutationDocument, variables: { id: id, input: variables } }]
  }
}

const insertJobTriggerMutationDocument = graphql(/* GraphQL */ `
  mutation insertJobTrigger($input: coassign_job_trigger_insert_input!) {
    insert_coassign_job_trigger_one(object: $input) {
      created_at
    }
  }
`)

const updateJobTriggerMutationDocument = graphql(/* GraphQL */ `
  mutation updateJobTrigger($id: String!, $input: coassign_job_trigger_set_input!) {
    update_coassign_job_trigger_by_pk(pk_columns: { id: $id }, _set: $input) {
      updated_at
    }
  }
`)
